import { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AuthContext from "../../context/authProvider";
import Loading from "../atoms/loading";
import { getArtLabelsQueue } from "./functions";
import { statusLabel } from "./status";
import { formatDay } from "../extendedArt/format";

const EMPTY = { items: [], totals: { arts: 0, orders: 0, late: 0 } };

/** Fila de artes (spec §4.5/§5.3): artes não aprovadas que pedidos ativos esperam, por prazo. */
const ArtLabelsQueue = () => {
    const { user } = useContext(AuthContext);
    const navigate = useNavigate();
    const [data, setData] = useState(EMPTY);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        let alive = true;
        (async () => {
            const out = await getArtLabelsQueue(user?.token);
            if (!alive) return;
            setLoading(false);
            if (out?.type === "success") {
                setData({ ...EMPTY, ...out.data });
                setError("");
            } else {
                setError(out?.message || "Falha ao carregar a fila");
            }
        })();
        return () => {
            alive = false;
        };
    }, [user?.token]);

    const goToArt = (artKey) => navigate(`/manafix/art-labels/${encodeURIComponent(artKey)}`);

    if (loading) return <Loading />;

    const { totals } = data;
    return (
        <>
            {error ? <p className="text-red-400">{error}</p> : null}
            <p className="mb-2 text-sm text-laranjinha/70">
                {`${totals.arts} arte(s) pendente(s) · ${totals.orders} pedido(s) afetado(s)${totals.late ? ` · ${totals.late} atrasado(s)` : ""}`}
            </p>
            <table className="w-full text-sm">
                <thead className="text-[#d59106]">
                    <tr>
                        <th scope="col">Arte</th>
                        <th scope="col" className="text-left">Carta</th>
                        <th scope="col" className="text-left">Modelos</th>
                        <th scope="col">Status</th>
                        <th scope="col">Pedido mais urgente</th>
                        <th scope="col">Prazo</th>
                        <th scope="col">Seriais esperando</th>
                    </tr>
                </thead>
                <tbody>
                    {data.items.map((a) => {
                        const late = Boolean(a.urgentOrder?.late) && Boolean(a.urgentOrder?.deadline);
                        return (
                            <tr
                                key={a.artKey}
                                className="cursor-pointer border-b border-[#333] hover:bg-[#1a1a1a]"
                                tabIndex={0}
                                role="link"
                                aria-label={`Abrir revisão de ${a.cardName}`}
                                onClick={() => goToArt(a.artKey)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        goToArt(a.artKey);
                                    }
                                }}
                            >
                                <td className="text-center">{a.thumbUrl ? <img alt="" src={a.thumbUrl} className="inline h-10 rounded" /> : "—"}</td>
                                <td>{a.cardName} <span className="text-xs text-gray-500">{a.artKey}</span></td>
                                <td>{(a.models || []).map((m) => m.name).join(", ")}</td>
                                <td className="text-center">{statusLabel(a.artStatus)}</td>
                                <td className="text-center">
                                    {a.urgentOrder?.id ? (
                                        <Link className="text-primary underline" to={`/manafix/extended-art/${a.urgentOrder.id}`} onClick={(e) => e.stopPropagation()}>
                                            {a.urgentOrder.userName || "Cliente sem nome"}
                                        </Link>
                                    ) : "—"}
                                </td>
                                <td className={`text-center ${late ? "font-semibold text-red-500" : ""}`}>
                                    {a.urgentOrder?.deadline ? `${formatDay(a.urgentOrder.deadline)}${late ? " (atrasado)" : ""}` : "sem prazo"}
                                </td>
                                <td className="text-center">{a.serialsWaiting}</td>
                            </tr>
                        );
                    })}
                    {!data.items.length ? (
                        <tr><td colSpan={7} className="py-4 text-center text-gray-500">Nenhuma arte pendente para pedidos ativos.</td></tr>
                    ) : null}
                </tbody>
            </table>
        </>
    );
};

export default ArtLabelsQueue;
